import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { storage } from '@/utils/storage';

// 1. 懒加载收集：eager: false → 返回 () => import(...)，按需加载
const loaders = import.meta.glob([
  '/src/**/language.js',
  '!/src/app/reducers/language.js', // 排除 Redux slice，避免被误当作翻译 namespace
]);

// 2. 内存缓存：namespace -> { language: { en, zh } }；null 表示该 namespace 无语言文件
const cache = new Map();

// 3. 正在加载中的 namespace 集合（并发去重）：namespace -> Promise
const pending = new Map();

// 加载单个 namespace 的语言包；返回 Promise（Suspense 用它挂起）
function loadNamespace(namespace) {
  if (cache.has(namespace)) return Promise.resolve(cache.get(namespace));
  if (pending.has(namespace)) return pending.get(namespace);

  const key = '/src/' + namespace.split('.').join('/') + '.js';
  const loader = loaders[key];

  if (!loader) {
    // 没有对应语言文件，标记为空，避免反复查找
    cache.set(namespace, null);
    return Promise.resolve(null);
  }

  const p = loader()
    .then((mod) => {
      const dict = (mod?.default ?? mod)?.language;
      cache.set(namespace, dict);
      return dict;
    })
    .catch(() => {
      cache.set(namespace, null);
      return null;
    })
    .finally(() => pending.delete(namespace));

  pending.set(namespace, p);
  return p;
}

// 同步读取已缓存的字典（未缓存返回 null）
const getDict = (namespace, lang) => {
  return cache.get(namespace)?.[lang];
};

// 核心翻译查询；key 未命中返回 key 本身
export const t = (namespace, key, lang = 'zh') => {
  const dict = getDict(namespace, lang);
  const value = dict?.[key];
  if (value === undefined) {
    if (process.env.NODE_ENV !== 'production' && dict) {
      console.warn(`[i18n] "${namespace}" 未找到 key: "${key}"`);
    }
    return key;
  }
  return value;
};

// 按当前语言预绑定 namespace
export const createI18n = (namespace) => (key) =>
  t(namespace, key, storage.get('language') || 'zh');

// 订阅语言变化；语言包未就绪时用 Suspense 挂起（显示 fallback），而非返回 key
export const useI18n = (namespace) => {
  const lang = useSelector((state) => state.lang.value);

  // 语言包尚未缓存时，抛出一个 Promise 让 React 挂起当前渲染
  // 由上游 <Suspense> 显示 fallback，就绪后自动重新渲染（不闪英文）
  if (!cache.has(namespace)) {
    throw loadNamespace(namespace);
  }

  return useCallback((key) => t(namespace, key, lang), [namespace, lang]);
};